import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbAlertLevel } from "abreact/feedback/AbAlertLevel";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbButton } from "abreact/misc/AbButton";
import { AbIcon } from "abreact/misc/AbIcon";
import { AbSize } from "abreact/misc/AbSize";
import { AbDialog } from "abreact/popup/AbDialog";
import { PopupManager } from "abreact/popup/PopupManager";
import { AbWebServices } from "abweb/service/AbWebServices";
import { AbClipboard } from "abweb/utils/clipboard/AbClipboard";
import * as React from "react";

/**
 * An enumeration representing possible levels of alerts to be shown to the user.
 * @deprecated Use abreact/feedback/AbAlertLevel
 */
export { AbAlertLevel as AlertLevel };

/**
 * Properties of the AbAlert component.
 */

/**
 * A map of alert types and information about rendering them.
 *
 * Note: the color for each alert type is defined in Alert.css
 * based upon the registered class name.
 */
const mapAlertLevel = alertLevel => {
  switch (alertLevel) {
    case AbAlertLevel.SUCCESS:
      return {
        defaultTitle: AbReactResources.alert_dialog_defaultTitle_success(),
        level: AbAlertLevel.SUCCESS,
        className: "ab-success",
        iconName: "absdk:success",
        iconAltText: AbReactResources.alert_dialog_iconAltText_success(),
        elementId: "ab-alert-ab-success-message"
      };
    case AbAlertLevel.INFO:
      return {
        defaultTitle: AbReactResources.alert_dialog_defaultTitle_info(),
        level: AbAlertLevel.INFO,
        className: "ab-info",
        iconName: "absdk:info-fill",
        iconAltText: AbReactResources.alert_dialog_iconAltText_info(),
        elementId: "ab-alert-ab-info-message"
      };
    case AbAlertLevel.WARNING:
      return {
        defaultTitle: AbReactResources.alert_dialog_defaultTitle_warning(),
        level: AbAlertLevel.WARNING,
        className: "ab-warning",
        iconName: "absdk:warning",
        iconAltText: AbReactResources.alert_dialog_iconAltText_warning(),
        elementId: "ab-alert-ab-warning-message"
      };
    case AbAlertLevel.ERROR:
      return {
        defaultTitle: AbReactResources.alert_dialog_defaultTitle_error(),
        level: AbAlertLevel.ERROR,
        className: "ab-error",
        iconName: "absdk:error",
        iconAltText: AbReactResources.alert_dialog_iconAltText_error(),
        elementId: "ab-alert-ab-error-message"
      };
  }
};
const alertClass = "ab-alert";
const alertMessageClass = "ab-alert-message";
const alertIconClass = "ab-alert-icon";

/**
 * The **AbAlert** component displays an alert message, either inline or as a popup (via methods).
 *
 * [[include:abreact/public/popup/AbAlert.md]]
 */
export function AbAlert(props) {
  const {
    embeddedInMyDialog,
    ...dialogContentProps
  } = props;
  const {
    level,
    message
  } = dialogContentProps;
  const alertType = mapAlertLevel(level);
  if (!embeddedInMyDialog) {
    return /*#__PURE__*/React.createElement("div", ReactUtils.componentProps(alertClass, props, [alertType.className, "ab-alert-stand-alone"]), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(AbIcon, {
      className: alertIconClass,
      image: alertType.iconName,
      altText: alertType.iconAltText,
      size: AbSize.MEDIUM
    })), /*#__PURE__*/React.createElement("div", {
      className: alertMessageClass
    }, message));
  }
  return /*#__PURE__*/React.createElement(AbAlertDialogContent, dialogContentProps);
}
const AbAlertDialogContent = props => {
  const {
    message,
    level
  } = props;
  const alertType = mapAlertLevel(level);

  // ab-alert and ab-alert-message are more "layers of divs" than Kim would like, but we're leaving
  // it like this because it's easier than fixing all the Cypress tests.
  return /*#__PURE__*/React.createElement("div", ReactUtils.componentProps(alertClass, props, [alertType.className]), /*#__PURE__*/React.createElement("div", {
    className: alertMessageClass
  }, message));
};
export const AbAlertDialog = props => {
  const {
    level,
    buttonBar,
    draggable,
    onClose
  } = props;
  const type = mapAlertLevel(level);
  const title = props.title ?? type.defaultTitle;
  const resizable = props.resizable ?? false;
  const commonProps = ReactUtils.extractCommonProps(props);
  return /*#__PURE__*/React.createElement(AbDialog, _extends({
    onClose: onClose,
    closeButton: false,
    buttonBar: buttonBar,
    icon: /*#__PURE__*/React.createElement(AbIcon, {
      className: alertIconClass,
      image: type.iconName,
      altText: props.title ? type.iconAltText : "",
      size: AbSize.LARGE
    }),
    title: /*#__PURE__*/React.createElement("div", {
      className: "ab-alert-title"
    }, title),
    message: false,
    draggable: draggable,
    resizable: resizable,
    maximizable: props.maximizable ?? false
  }, commonProps, {
    className: ReactUtils.componentClassNamesString("ab-alert-dialog", commonProps, [type.className])
  }), /*#__PURE__*/React.createElement(AbAlertDialogContent, props));
};
(function (_AbAlert) {
  const show = _AbAlert.show = props => {
    return PopupManager.show(/*#__PURE__*/React.createElement(AbAlertDialog, props));
  };
  const showInfo = _AbAlert.showInfo = (message, title) => {
    return show({
      level: AbAlertLevel.INFO,
      title: title,
      message: message
    });
  };
  const showSuccess = _AbAlert.showSuccess = (message, title) => {
    return show({
      level: AbAlertLevel.SUCCESS,
      title: title,
      message: message
    });
  };
  const showWarning = _AbAlert.showWarning = (message, title) => {
    return show({
      level: AbAlertLevel.WARNING,
      title: title,
      message: message
    });
  };
  const showError = _AbAlert.showError = (message, title) => {
    return show({
      level: AbAlertLevel.ERROR,
      title: title,
      message: message
    });
  };
  const showErrorWithCopyToClipboardOption = _AbAlert.showErrorWithCopyToClipboardOption = (message, title) => {
    let calcSafeClipboard = () => {
      try {
        return AbWebServices.clipboard;
      } catch (e) {
        // Assume AbWebServices is not yet initialized
        return new AbClipboard();
      }
    };
    let clipboard = calcSafeClipboard();
    let onCopyClicked = async () => {
      clipboard.setData("text", message);
    };
    const content = /*#__PURE__*/React.createElement("div", {
      className: 'long-error-text-wrapper'
    }, /*#__PURE__*/React.createElement("div", null, message), /*#__PURE__*/React.createElement(AbButton, {
      className: 'copy-button',
      label: AbReactResources.abCopyButton_label(),
      onClick: onCopyClicked
    }));
    return show({
      level: AbAlertLevel.ERROR,
      title: title,
      message: content,
      resizable: true,
      maximizable: true,
      className: "ab-show-error-with-copy-option"
    });
  };
})(AbAlert || (AbAlert = {}));
//# sourceMappingURL=AbAlert.js.map
